import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import confetti from "canvas-confetti";
import {
  Lock, CheckCircle2, PlayCircle, ArrowRight, ArrowLeft, Lightbulb, ListChecks,
  Award, Sparkles, ChevronRight, Target, BarChart3, Download, Share2, Rocket, GraduationCap, RotateCcw,
} from "lucide-react";
import { LESSONS, MODULES } from "./courseData";
import { TOOLS } from "./Tools";

const NAVY = "#001540";
const GOLD = "#f5c84c";
const GREEN = "#a7cb90";
const KEY = "salesfirst_v1";

const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; } };
const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function SalesFirstPage() {
  const navigate = useNavigate();
  const [state, setState] = useState(load);
  const [view, setView] = useState("intro"); // intro | dashboard | lesson | cert
  const [lessonIdx, setLessonIdx] = useState(0);
  const [nameInput, setNameInput] = useState(state.name || "");

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(state)); }, [state]);
  useEffect(() => { window.scrollTo(0, 0); }, [view, lessonIdx]);
  // Oculta widgets flotantes de terceros (chat) mientras se usa el curso
  useEffect(() => {
    const st = document.createElement("style");
    st.innerHTML = "chat-widget,.lc-chat-widget,#lc_text-widget{display:none !important;}";
    document.head.appendChild(st);
    return () => { document.head.removeChild(st); };
  }, []);

  const done = state.lessonDone || {};
  const doneCount = LESSONS.filter((l) => done[l.id]).length;
  const progress = Math.round((doneCount / LESSONS.length) * 100);
  const allDone = doneCount === LESSONS.length;

  const lessonUnlocked = (i) => i === 0 || !!done[LESSONS[i - 1].id];
  const moduleComplete = (n) => LESSONS.filter((l) => l.module === n).every((l) => done[l.id]);
  const moduleUnlocked = (n) => n === 0 || moduleComplete(n - 1);
  const firstPendingIdx = Math.max(0, LESSONS.findIndex((l) => !done[l.id]));

  const setTool = (toolKey, val) => setState((s) => ({ ...s, tools: { ...(s.tools || {}), [toolKey]: val } }));
  const markDone = (id) => setState((s) => ({ ...s, lessonDone: { ...(s.lessonDone || {}), [id]: true } }));
  const toggleAction = (id) => setState((s) => ({ ...s, actions: { ...(s.actions || {}), [id]: !(s.actions || {})[id] } }));

  const startCourse = () => {
    const nm = nameInput.trim();
    if (nm) setState((s) => ({ ...s, name: nm }));
    setView("dashboard");
  };
  const resetCourse = () => {
    if (!window.confirm("¿Empezar el curso de nuevo? Se borrará tu progreso, tus quizzes y los datos de las herramientas. Esto no se puede deshacer.")) return;
    const nm = state.name || "";
    const fresh = nm ? { name: nm } : {};
    setState(fresh);
    setLessonIdx(0);
    setView("dashboard");
    window.scrollTo(0, 0);
  };
  const openLesson = (i) => { if (lessonUnlocked(i)) { setLessonIdx(i); setView("lesson"); } };

  if (view === "intro") return <Intro name={state.name} nameInput={nameInput} setNameInput={setNameInput} onStart={startCourse} progress={progress} />;
  if (view === "cert") return <Certificate name={state.name} onBack={() => setView("dashboard")} />;
  if (view === "lesson") {
    return (
      <LessonView
        idx={lessonIdx} state={state} setTool={setTool} markDone={markDone} done={done}
        actions={state.actions || {}} toggleAction={toggleAction}
        onPrev={() => lessonIdx > 0 && lessonUnlocked(lessonIdx - 1) ? setLessonIdx(lessonIdx - 1) : setView("dashboard")}
        onNext={() => { const ni = lessonIdx + 1; if (ni < LESSONS.length && lessonUnlocked(ni)) setLessonIdx(ni); else setView("dashboard"); }}
        onHome={() => setView("dashboard")}
      />
    );
  }

  // ───── DASHBOARD ─────
  const meta = state.tools?.meta;
  const metaReady = meta && meta.ganar && meta.porVenta && meta.cierre && meta.dias;
  const personasDia = metaReady ? Math.ceil(Math.ceil(Number(meta.ganar) / Number(meta.porVenta)) / (Number(meta.cierre) / 100) / Number(meta.dias)) : null;
  const diarioEntries = state.tools?.diario?.entries || [];
  const weekAgo = localDay(new Date(Date.now() - 7 * 86400000));
  const week = diarioEntries.filter((e) => e.date >= weekAgo);
  const wsum = (k) => week.reduce((a, e) => a + (Number(e[k]) || 0), 0);

  return (
    <div className="min-h-screen" style={{ background: "#f7f8fb", fontFamily: "'Poppins', sans-serif", color: "#1a1f36" }} data-testid="salesfirst-dashboard">
      <TopBar onExit={() => navigate("/academia")} />
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 pb-28">
        <h1 className="text-2xl sm:text-3xl font-extrabold" style={{ color: NAVY }}>
          ¡Hola{state.name ? `, ${state.name}` : ""}! <span className="inline-block">👋</span>
        </h1>
        <p className="text-slate-500 mt-1">Tu meta está primero que la plataforma. Avancemos un paso hoy.</p>

        {/* Progress */}
        <div className="mt-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold" style={{ color: NAVY }}>Progreso del curso</span>
            <span className="text-sm font-extrabold" style={{ color: NAVY }}>{progress}%</span>
          </div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${progress}%`, background: GREEN }} data-testid="sf-progress-bar" />
          </div>
          <button onClick={() => openLesson(firstPendingIdx)} data-testid="sf-continue-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 font-extrabold transition-transform active:scale-[0.98]"
            style={{ background: GOLD, color: NAVY }}>
            {doneCount === 0 ? "Empezar la primera lección" : allDone ? "Repasar lecciones" : "Continuar donde lo dejé"} <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Meta + Números */}
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="sf-card-meta">
            <div className="flex items-center gap-2 mb-2"><Target className="h-4 w-4" style={{ color: GOLD }} /><span className="text-sm font-bold" style={{ color: NAVY }}>Mi meta del mes</span></div>
            {metaReady ? (
              <p className="text-slate-700 text-sm leading-relaxed">Para ganar <strong>${Number(meta.ganar).toLocaleString()}</strong> debes hablar con <strong style={{ color: NAVY }}>{personasDia} personas al día</strong>.</p>
            ) : (
              <p className="text-slate-400 text-sm">Completa la calculadora de meta (Módulo 6) para verla aquí.</p>
            )}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="sf-card-numeros">
            <div className="flex items-center gap-2 mb-2"><BarChart3 className="h-4 w-4" style={{ color: GOLD }} /><span className="text-sm font-bold" style={{ color: NAVY }}>Mis números de la semana</span></div>
            {week.length ? (
              <div className="grid grid-cols-4 gap-1 text-center">
                {[["Cont.", "contactos"], ["Conv.", "conversaciones"], ["Cotiz.", "cotizaciones"], ["Ventas", "ventas"]].map(([l, k]) => (
                  <div key={k}><p className="text-lg font-black" style={{ color: NAVY }}>{wsum(k)}</p><p className="text-[10px] text-slate-400">{l}</p></div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm">Registra tu día en el Diario de ventas (Módulo 5).</p>
            )}
          </div>
        </div>

        {/* Modules */}
        <h2 className="mt-7 mb-3 text-lg font-extrabold" style={{ color: NAVY }}>Los 6 módulos</h2>
        <div className="space-y-5">
          {MODULES.map((m) => {
            const unlocked = moduleUnlocked(m.n);
            const complete = moduleComplete(m.n);
            const lessons = LESSONS.map((l, i) => ({ l, i })).filter((x) => x.l.module === m.n);
            return (
              <div key={m.n} className={`rounded-2xl border shadow-sm overflow-hidden ${unlocked ? "bg-white border-slate-200" : "bg-slate-100 border-slate-200 opacity-70"}`} data-testid={`sf-module-${m.n}`}>
                <div className="flex items-center gap-3 p-4" style={complete ? { background: "#a7cb9018" } : {}}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-black text-white" style={{ background: complete ? GREEN : unlocked ? NAVY : "#cbd5e1" }}>
                    {complete ? <CheckCircle2 className="h-5 w-5" /> : unlocked ? m.n : <Lock className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold leading-tight" style={{ color: NAVY }}>Módulo {m.n} · {m.title}</p>
                    <p className="text-xs text-slate-400">{m.subtitle}</p>
                  </div>
                  {!unlocked && <span className="text-[11px] font-bold text-slate-400">Bloqueado</span>}
                </div>
                {unlocked && (
                  <div className="px-4 pb-3 space-y-1">
                    {lessons.map(({ l, i }) => {
                      const lUnlocked = lessonUnlocked(i);
                      const lDone = done[l.id];
                      return (
                        <button key={l.id} disabled={!lUnlocked} onClick={() => openLesson(i)} data-testid={`sf-lesson-link-${l.id}`}
                          className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${lUnlocked ? "hover:bg-slate-50" : "opacity-50 cursor-not-allowed"}`}>
                          {lDone ? <CheckCircle2 className="h-5 w-5 flex-shrink-0" style={{ color: GREEN }} /> : lUnlocked ? <PlayCircle className="h-5 w-5 flex-shrink-0" style={{ color: GOLD }} /> : <Lock className="h-4 w-4 flex-shrink-0 text-slate-400" />}
                          <span className="flex-1 min-w-0 text-sm font-semibold truncate" style={{ color: NAVY }}>{l.id} · {l.title}</span>
                          <span className="text-[11px] text-slate-400 flex-shrink-0">{l.duration}</span>
                          <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Certificate CTA */}
        <div className="mt-7 rounded-2xl p-5 text-center" style={{ background: allDone ? NAVY : "#e2e8f0" }}>
          <Award className="h-8 w-8 mx-auto mb-2" style={{ color: allDone ? GOLD : "#94a3b8" }} />
          {allDone ? (
            <>
              <p className="text-white font-extrabold text-lg">¡Completaste el curso! 🎉</p>
              <p className="text-slate-300 text-sm mt-1 mb-3">Descarga tu certificado Sales First.</p>
              <button onClick={() => setView("cert")} data-testid="sf-cert-cta"
                className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 font-extrabold" style={{ background: GOLD, color: NAVY }}>
                Ver mi certificado <ArrowRight className="h-4 w-4" />
              </button>
            </>
          ) : (
            <p className="text-slate-500 font-semibold text-sm">Completa los 6 módulos para desbloquear tu certificado.</p>
          )}
        </div>

        {/* Reiniciar curso */}
        {doneCount > 0 && (
          <div className="mt-6 text-center">
            <button onClick={resetCourse} data-testid="sf-reset-btn"
              className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 font-bold text-sm border-2 border-slate-200 text-slate-500 hover:text-red-500 hover:border-red-200 transition-colors">
              <RotateCcw className="h-4 w-4" /> Empezar de nuevo
            </button>
            <p className="text-[11px] text-slate-400 mt-2">Borra tu progreso y los datos de las herramientas para repetir el curso desde cero.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────── TOP BAR ───────────────── */
function TopBar({ onExit }) {
  return (
    <div className="sticky top-0 z-30 flex items-center justify-between px-4 py-3" style={{ background: NAVY }}>
      <button onClick={onExit} className="text-white/80 hover:text-white inline-flex items-center gap-1.5 text-sm font-semibold" data-testid="sf-exit">
        <ArrowLeft className="h-4 w-4" /> Academia
      </button>
      <span className="font-extrabold text-sm sm:text-base" style={{ color: "#fff" }}>Club<span style={{ color: GOLD }}>Viajemos</span> · Sales First</span>
      <span className="w-16" />
    </div>
  );
}

/* ───────────────── INTRO / LANDING ───────────────── */
function Intro({ name, nameInput, setNameInput, onStart, progress }) {
  const navigate = useNavigate();
  const goals = [
    "Entender la mentalidad Sales First.",
    "Crear tu estadística de ventas.",
    "Diseñar tu rutina de 90 minutos al día.",
    "Definir tu meta mensual y cuántas personas contactar cada día.",
  ];
  return (
    <div className="min-h-screen" style={{ background: "#f7f8fb", fontFamily: "'Poppins', sans-serif", color: "#1a1f36" }} data-testid="salesfirst-intro">
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: NAVY }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1600&q=80')" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,21,64,0.92), rgba(0,21,64,0.75))" }} />
        <div className="relative max-w-3xl mx-auto px-4 pt-12 pb-14 text-center">
          <div className="text-left mb-6">
            <button onClick={() => navigate("/academia")} className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" /> Volver a la Academia</button>
          </div>
          <span className="inline-block text-xs font-bold uppercase tracking-[2px] px-4 py-1.5 rounded-full mb-5" style={{ background: GOLD, color: NAVY }}>Curso de inicio · Club Viajemos</span>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white leading-[1.05]">
            Antes de aprender una plataforma, aprende <span style={{ color: GOLD }}>para qué la vas a usar: para vender.</span>
          </h1>
          <p className="text-slate-200 mt-4 text-base sm:text-lg">Sales First · Tu meta antes que la plataforma</p>

          <div className="mt-8 max-w-sm mx-auto">
            <input
              value={nameInput} onChange={(e) => setNameInput(e.target.value)} placeholder="Escribe tu nombre"
              data-testid="sf-name-input"
              className="w-full rounded-xl px-4 py-3 text-center font-semibold text-[#1a1f36] outline-none border-2 border-transparent focus:border-[#f5c84c]"
            />
            <button onClick={onStart} data-testid="sf-start-btn"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 font-extrabold text-lg transition-transform active:scale-[0.98]"
              style={{ background: GOLD, color: NAVY }}>
              <Rocket className="h-5 w-5" /> {progress > 0 ? "Continuar mi curso" : "Empezar mi curso"}
            </button>
            <p className="text-white/60 text-xs mt-3">Unos 90 minutos · 6 módulos · certificado al terminar</p>
          </div>
        </div>
      </section>

      {/* QUÉ VAS A LOGRAR */}
      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-center text-2xl font-extrabold mb-6" style={{ color: NAVY }}>Qué vas a lograr</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {goals.map((g, i) => (
            <div key={i} className="flex items-start gap-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <span className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center font-black text-white" style={{ background: GREEN }}>{i + 1}</span>
              <p className="font-semibold text-sm" style={{ color: NAVY }}>{g}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="max-w-4xl mx-auto px-4 pb-10">
        <div className="rounded-2xl p-6 text-center" style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
          <GraduationCap className="h-8 w-8 mx-auto mb-3" style={{ color: GOLD }} />
          <p className="font-semibold text-slate-600">6 módulos · lecciones de 5 a 10 minutos · herramientas interactivas · certificado al terminar.</p>
        </div>
      </section>

      {/* FRASE CIERRE */}
      <section className="max-w-3xl mx-auto px-4 pb-16">
        <div className="rounded-3xl p-8 text-center" style={{ background: NAVY }}>
          <Sparkles className="h-7 w-7 mx-auto mb-4" style={{ color: GOLD }} />
          <p className="text-xl sm:text-2xl font-extrabold text-white leading-snug">
            La plataforma es el vehículo, la educación es el mapa, pero <span style={{ color: GOLD }}>tú eres el motor.</span> Y el combustible son tus clientes.
          </p>
        </div>
      </section>

      <footer className="text-center py-6 text-xs text-slate-400">
        © Club Viajemos ·{" "}
        <button onClick={() => navigate("/privacidad")} className="underline">Privacidad</button> ·{" "}
        <button onClick={() => navigate("/terminos")} className="underline">Términos</button>
      </footer>
    </div>
  );
}

/* ───────────────── BODY RENDERER ───────────────── */
function Block({ b, lesson, state, setTool }) {
  if (b.t === "p") return <p className="text-slate-700 leading-relaxed mb-3 whitespace-pre-line">{b.c}</p>;
  if (b.t === "h") return <h4 className="font-extrabold text-lg mt-4 mb-2" style={{ color: NAVY }}>{b.c}</h4>;
  if (b.t === "ul") return <ul className="list-disc pl-5 space-y-1.5 mb-3 text-slate-700">{b.items.map((x, i) => <li key={i}>{x}</li>)}</ul>;
  if (b.t === "ol") return <ol className="list-decimal pl-5 space-y-1.5 mb-3 text-slate-700">{b.items.map((x, i) => <li key={i}>{x}</li>)}</ol>;
  if (b.t === "quote") return (
    <div className="my-4 rounded-2xl p-5" style={{ background: "#f5c84c1f", borderLeft: `4px solid ${GOLD}` }}>
      <p className="font-bold whitespace-pre-line" style={{ color: NAVY }}>{b.c}</p>
    </div>
  );
  if (b.t === "table") return (
    <div className="my-4 overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm border-collapse">
        <thead><tr style={{ background: NAVY }}>{b.head.map((h, i) => <th key={i} className="text-left font-bold text-white px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{b.rows.map((r, ri) => <tr key={ri} className={ri % 2 ? "bg-slate-50" : "bg-white"}>{r.map((c, ci) => <td key={ci} className={`px-3 py-2.5 border-t border-slate-100 ${ci === 0 ? "font-semibold" : "text-slate-600"}`} style={ci === 0 ? { color: NAVY } : {}}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
  if (b.t === "funnel") {
    const stages = [["Contactos", 100], ["Conversaciones", 78], ["Cotizaciones", 52], ["Ventas", 30]];
    return (
      <div className="my-5 space-y-2">
        {stages.map(([l, w], i) => (
          <div key={l} className="mx-auto rounded-xl py-3 text-center font-bold text-white transition-all" style={{ width: `${w}%`, background: `linear-gradient(90deg, ${NAVY}, ${i === 3 ? GREEN : "#1d3a6b"})` }}>{l}</div>
        ))}
      </div>
    );
  }
  if (b.t === "cycle") {
    const steps = [["Vende", "habla con clientes todos los días"], ["Anota", "contactos, cotizaciones y ventas"], ["Analiza", "mira tus números al final de la semana"], ["Aprende", "de errores y aciertos"], ["Ajusta", "cambia UNA cosa y vuelve a medir"]];
    return (
      <div className="my-5 space-y-2">
        {steps.map(([t, d], i) => (
          <div key={t} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 bg-white">
            <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-black text-white" style={{ background: GREEN }}>{i + 1}</span>
            <p className="text-sm text-slate-700"><strong style={{ color: NAVY }}>{t}:</strong> {d}</p>
          </div>
        ))}
      </div>
    );
  }
  if (b.t === "tool" && lesson.tool) {
    const ToolComp = TOOLS[lesson.tool];
    if (lesson.tool === "plantillas") return <div className="my-5"><ToolComp /></div>;
    return <div className="my-5"><ToolComp value={state.tools?.[lesson.tool] || {}} onChange={(v) => setTool(lesson.tool, v)} /></div>;
  }
  return null;
}

/* ───────────────── LESSON VIEW ───────────────── */
function LessonView({ idx, state, setTool, markDone, done, actions, toggleAction, onPrev, onNext, onHome }) {
  const lesson = LESSONS[idx];
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { setAnswers({}); setSubmitted(false); }, [idx]);

  const correctCount = lesson.quiz.filter((q, i) => answers[i] === q.correct).length;
  const passed = submitted && correctCount >= 2;
  const isDone = done[lesson.id];

  const submit = () => {
    setSubmitted(true);
    if (lesson.quiz.filter((q, i) => answers[i] === q.correct).length >= 2) markDone(lesson.id);
  };
  const retry = () => { setAnswers({}); setSubmitted(false); };

  return (
    <div className="min-h-screen" style={{ background: "#f7f8fb", fontFamily: "'Poppins', sans-serif", color: "#1a1f36" }} data-testid={`salesfirst-lesson-${lesson.id}`}>
      <TopBar onExit={() => navigate("/academia")} />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button onClick={onHome} className="text-slate-500 hover:text-[#001540] text-sm inline-flex items-center gap-1.5 mb-4"><ArrowLeft className="h-4 w-4" /> Todos los módulos</button>

        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}>Lección {lesson.id} · {lesson.duration}</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold mt-1 mb-4" style={{ color: NAVY }}>{lesson.title}</h1>

        {/* Video / image slot */}
        <div className="rounded-2xl overflow-hidden mb-5 border border-slate-200">
          {lesson.video ? (
            <div className="aspect-video"><iframe src={lesson.video} title={lesson.title} className="w-full h-full" allowFullScreen frameBorder="0" /></div>
          ) : (
            <div className="aspect-video bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80')" }} />
          )}
        </div>

        {/* Body */}
        <div className="mb-2">
          {lesson.body.map((b, i) => <Block key={i} b={b} lesson={lesson} state={state} setTool={setTool} />)}
        </div>

        {/* Idea clave */}
        <div className="my-5 rounded-2xl p-5 flex gap-3" style={{ background: "#f5c84c1f" }}>
          <Lightbulb className="h-6 w-6 flex-shrink-0" style={{ color: GOLD }} />
          <div><p className="text-xs font-bold uppercase tracking-wider" style={{ color: NAVY }}>Idea clave</p><p className="font-semibold mt-0.5" style={{ color: NAVY }}>{lesson.ideaClave}</p></div>
        </div>

        {/* Acción de hoy */}
        <button onClick={() => toggleAction(lesson.id)} data-testid="sf-accion-toggle"
          className="w-full text-left my-5 rounded-2xl p-5 flex gap-3 border-2 transition-colors" style={{ background: "#a7cb901f", borderColor: actions[lesson.id] ? GREEN : "transparent" }}>
          <span className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center mt-0.5 ${actions[lesson.id] ? "text-white" : "bg-white text-slate-300"}`} style={actions[lesson.id] ? { background: GREEN } : {}}><ListChecks className="h-4 w-4" /></span>
          <div><p className="text-xs font-bold uppercase tracking-wider" style={{ color: NAVY }}>Tu acción de hoy</p><p className="font-semibold mt-0.5" style={{ color: NAVY }}>{lesson.accion}</p></div>
        </button>

        {/* Quiz */}
        <div className="my-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="sf-quiz">
          <p className="font-extrabold mb-1" style={{ color: NAVY }}>Mini quiz · acierta 2 de 3</p>
          <p className="text-xs text-slate-400 mb-4">Responde para completar la lección.</p>
          <div className="space-y-5">
            {lesson.quiz.map((q, qi) => (
              <div key={qi}>
                <p className="font-semibold text-sm mb-2" style={{ color: NAVY }}>{qi + 1}. {q.q}</p>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const sel = answers[qi] === oi;
                    let style = { borderColor: "#e2e8f0" };
                    if (submitted) {
                      if (oi === q.correct) style = { borderColor: GREEN, background: "#a7cb9018" };
                      else if (sel) style = { borderColor: "#ef4444", background: "#fef2f2" };
                    } else if (sel) style = { borderColor: GOLD, background: "#f5c84c14" };
                    return (
                      <button key={oi} disabled={submitted} onClick={() => setAnswers({ ...answers, [qi]: oi })}
                        data-testid={`sf-quiz-${qi}-opt-${oi}`}
                        className="w-full text-left rounded-xl border-2 px-4 py-2.5 text-sm font-medium transition-colors" style={style}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {submitted && <p className="text-xs mt-2 text-slate-500">{answers[qi] === q.correct ? "✅ Correcto. " : "❌ Revisa: "}{q.exp}</p>}
              </div>
            ))}
          </div>

          {!submitted ? (
            <button onClick={submit} disabled={Object.keys(answers).length < lesson.quiz.length} data-testid="sf-quiz-submit"
              className="mt-5 w-full rounded-xl py-3 font-extrabold disabled:opacity-40 transition-transform active:scale-[0.98]" style={{ background: GOLD, color: NAVY }}>
              Revisar respuestas
            </button>
          ) : passed ? (
            <div className="mt-5 rounded-xl p-4 text-center" style={{ background: "#a7cb9022" }} data-testid="sf-quiz-passed">
              <p className="font-extrabold" style={{ color: NAVY }}>¡Aprobaste! {correctCount}/3 ✅</p>
              <p className="text-sm text-slate-600 mt-0.5">Lección completada. Sigue avanzando.</p>
            </div>
          ) : (
            <div className="mt-5 rounded-xl p-4 text-center bg-red-50" data-testid="sf-quiz-failed">
              <p className="font-extrabold text-red-600">{correctCount}/3 · Necesitas 2 de 3</p>
              <button onClick={retry} className="mt-2 rounded-lg px-4 py-2 font-bold text-sm" style={{ background: NAVY, color: "#fff" }} data-testid="sf-quiz-retry">Intentar de nuevo</button>
            </div>
          )}
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between gap-3 pb-28">
          <button onClick={onPrev} className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 font-bold text-sm border-2 border-slate-200 text-slate-600" data-testid="sf-prev">
            <ArrowLeft className="h-4 w-4" /> Anterior
          </button>
          <button onClick={onNext} disabled={!isDone && !passed} data-testid="sf-next"
            className="inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 font-extrabold text-sm disabled:opacity-40" style={{ background: GOLD, color: NAVY }}>
            Siguiente <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── CERTIFICATE ───────────────── */
function Certificate({ name, onBack }) {
  const navigate = useNavigate();
  const fired = useRef(false);
  const studentName = (name || "Agente Club Viajemos").toUpperCase();
  const dateStr = useMemo(() => new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }), []);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    const end = Date.now() + 1500;
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: [GOLD, GREEN, "#fff"] });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: [GOLD, GREEN, "#fff"] });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  }, []);

  const download = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const W = 297, H = 210;
    doc.setFillColor(0, 21, 64); doc.rect(0, 0, W, H, "F");
    doc.setFillColor(255, 255, 255); doc.rect(10, 10, W - 20, H - 20, "F");
    doc.setDrawColor(245, 200, 76); doc.setLineWidth(1.5); doc.rect(14, 14, W - 28, H - 28);
    doc.setTextColor(0, 21, 64);
    doc.setFont("helvetica", "bold"); doc.setFontSize(14);
    doc.text("CLUB VIAJEMOS", W / 2, 34, { align: "center" });
    doc.setFontSize(26); doc.text("Certificado de finalización", W / 2, 58, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(13); doc.setTextColor(90, 90, 90);
    doc.text("Sales First · Curso de Inicio Club Viajemos", W / 2, 70, { align: "center" });
    doc.setFontSize(12); doc.text("Otorgado a:", W / 2, 92, { align: "center" });
    doc.setFont("helvetica", "bold"); doc.setFontSize(30); doc.setTextColor(0, 21, 64);
    doc.text(studentName, W / 2, 108, { align: "center" });
    doc.setDrawColor(167, 203, 144); doc.setLineWidth(0.8); doc.line(W / 2 - 55, 114, W / 2 + 55, 114);
    doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(90, 90, 90);
    doc.text("Por completar los 6 módulos y adoptar la mentalidad Sales First:", W / 2, 128, { align: "center" });
    doc.text("primero las ventas. La plataforma es el vehículo; tú eres el motor.", W / 2, 135, { align: "center" });
    doc.setTextColor(0, 21, 64); doc.setFontSize(10);
    doc.text(`Fecha: ${dateStr}`, 40, 175);
    doc.setDrawColor(0, 21, 64); doc.line(195, 170, 257, 170);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text('Carlos "Miami" Fernández', 226, 176, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text("Fundador y CEO · Club Viajemos", 226, 182, { align: "center" });
    doc.save("Certificado-SalesFirst-ClubViajemos.pdf");
  };

  const share = () => {
    const txt = encodeURIComponent("¡Completé el curso Sales First de Club Viajemos! Primero las ventas.");
    window.open(`https://api.whatsapp.com/send?text=${txt}`, "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: NAVY, fontFamily: "'Poppins', sans-serif" }} data-testid="salesfirst-certificate">
      <TopBar onExit={() => navigate("/academia")} />
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <Award className="h-14 w-14 mb-4" style={{ color: GOLD }} />
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white text-center">¡Felicidades{name ? `, ${name}` : ""}! 🎉</h1>
        <p className="text-slate-300 mt-2 text-center max-w-md">Completaste <strong style={{ color: GOLD }}>Sales First</strong>. Hoy empieza tu camino como máquina de ventas en la industria del turismo.</p>

        {/* Preview card */}
        <div className="mt-8 w-full max-w-xl bg-white rounded-2xl p-6 text-center shadow-2xl" style={{ border: `3px solid ${GOLD}` }}>
          <p className="text-xs font-bold uppercase tracking-[2px]" style={{ color: GOLD }}>Club Viajemos</p>
          <p className="text-lg font-extrabold mt-3" style={{ color: NAVY }}>Certificado de finalización</p>
          <p className="text-sm text-slate-500">Sales First · Curso de Inicio</p>
          <p className="text-2xl font-black mt-4" style={{ color: NAVY }}>{studentName}</p>
          <div className="mx-auto my-2 h-0.5 w-40" style={{ background: GREEN }} />
          <p className="text-xs text-slate-400 mt-3">{dateStr} · Carlos "Miami" Fernández, Fundador y CEO</p>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full max-w-xl">
          <button onClick={download} data-testid="sf-cert-download" className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl py-3 font-extrabold" style={{ background: GOLD, color: NAVY }}>
            <Download className="h-5 w-5" /> Descargar PDF
          </button>
          <button onClick={share} data-testid="sf-cert-share" className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl py-3 font-extrabold text-white" style={{ background: "#25d366" }}>
            <Share2 className="h-5 w-5" /> Compartir en WhatsApp
          </button>
        </div>
        <button onClick={onBack} className="mt-5 text-white/70 hover:text-white text-sm">← Volver al panel</button>
      </div>
    </div>
  );
}
